import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PencilIcon, TrashIcon, PlusIcon, ArrowPathIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../utils/simpleApiClient';
import SuperAdminLayout from '../../components/admin/SuperAdminLayout';

interface ScoringRule {
  id?: number;
  rule_id: string;
  name: string;
  description?: string;
  category: string;
  conditions: Array<{
    field: string;
    operator: string;
    value: any;
    value2?: any;
  }>;
  scoring: {
    base_score: number;
    adjustments: Array<{
      condition: {
        operator: string;
        value: any;
        value2?: any;
      };
      score: number;
    }>;
  };
  priority: number;
  enabled: boolean;
}

interface Category {
  name: string;
  weight: number;
  fields: Record<string, string>;
}

const GeneralAnalysisScoringPage: React.FC = () => {
  const [rules, setRules] = useState<ScoringRule[]>([]);
  const [categories, setCategories] = useState<Record<string, Category>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingRule, setEditingRule] = useState<ScoringRule | null>(null);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const data = await apiClient.get('/api/admin/general-analysis-scoring/rules') as { rules: ScoringRule[], categories: Record<string, Category> };
      setRules(data.rules);
      setCategories(data.categories);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load scoring rules');
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleEditRule = (rule: ScoringRule) => {
    setEditingRule(rule);
    setShowRuleModal(true);
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      await apiClient.delete(`/api/admin/general-analysis-scoring/rules/${ruleId}`);
      toast.success('Rule deleted successfully');
      fetchRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  const handleSaveRule = async (rule: ScoringRule) => {
    try {
      const url = rule.id
        ? `/api/admin/general-analysis-scoring/rules/${rule.rule_id}`
        : '/api/admin/general-analysis-scoring/rules';

      if (rule.id) {
        await apiClient.put(url, rule);
      } else {
        await apiClient.post(url, rule);
      }

      toast.success(`Rule ${rule.id ? 'updated' : 'created'} successfully`);
      setShowRuleModal(false);
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    }
  };

  const handleResetToDefaults = async () => {
    if (!confirm('This will reset all rules to their default values. Are you sure?')) return;

    try {
      const data = await apiClient.post('/api/admin/general-analysis-scoring/reset-defaults') as { message: string };
      toast.success(data.message);
      fetchRules();
    } catch (error) {
      console.error('Error resetting rules:', error);
      toast.error('Failed to reset rules');
    }
  };

  const categoryColors: Record<string, string> = {
    technical_infrastructure: 'border-blue-500/30 bg-blue-500/5',
    content_ai_readability: 'border-green-500/30 bg-green-500/5',
    security_compliance: 'border-yellow-500/30 bg-yellow-500/5',
    ai_specific_enhancements: 'border-purple-500/30 bg-purple-500/5',
    monitoring_feedback: 'border-indigo-500/30 bg-indigo-500/5',
  };

  const categoryIcons: Record<string, string> = {
    technical_infrastructure: '⚙️',
    content_ai_readability: '📝',
    security_compliance: '🔒',
    ai_specific_enhancements: '🤖',
    monitoring_feedback: '📊',
  };

  const operatorLabels: Record<string, string> = {
    equals: '=',
    not_equals: '≠',
    greater_than: '>',
    less_than: '<',
    between: 'between',
    contains: 'contains',
    exists: 'exists',
    not_exists: 'not exists',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <AdjustmentsHorizontalIcon className="h-8 w-8 text-[#6366f1]" />
                <h1 className="text-3xl font-bold text-white">General Analysis Scoring Rules</h1>
              </div>
              <p className="text-[#94a3b8] text-sm">Configure scoring rules for website general analysis factors</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditingRule({
                    rule_id: '',
                    name: '',
                    category: 'technical_infrastructure',
                    conditions: [],
                    scoring: { base_score: 50, adjustments: [] },
                    priority: 0,
                    enabled: true,
                  });
                  setShowRuleModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white rounded-lg hover:shadow-lg hover:shadow-[#6366f1]/25 transition-all duration-200 transform hover:-translate-y-0.5 font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Add Rule
              </button>
              <button
                onClick={handleResetToDefaults}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#374151] text-[#cbd5e1] rounded-lg hover:bg-[#4b5563] hover:text-white transition-all duration-200 font-medium border border-[#4b5563]"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-4">
          {Object.entries(categories).map(([categoryKey, category]) => {
            const categoryRules = rules.filter(r => r.category === categoryKey);
            const isExpanded = expandedCategories.has(categoryKey);

            return (
              <div
                key={categoryKey}
                className={`border rounded-xl overflow-hidden transition-all duration-200 ${categoryColors[categoryKey]} backdrop-blur-sm`}
              >
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{categoryIcons[categoryKey]}</span>
                    <div className="text-left">
                      <h2 className="text-xl font-semibold text-white">{category.name}</h2>
                      <p className="text-sm text-[#94a3b8]">
                        Weight: <span className="text-[#60a5fa] font-semibold">{category.weight}%</span> • 
                        <span className="ml-2">{categoryRules.length} rules</span> • 
                        <span className="ml-2 text-[#10b981]">{categoryRules.filter(r => r.enabled).length} enabled</span>
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-[#cbd5e1]" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-[#cbd5e1]" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-6 pb-4">
                    <div className="bg-[#1e293b] rounded-lg border border-[#374151] overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-[#334155] border-b border-[#374151]">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Rule Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Conditions</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Base Score</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Priority</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#374151]">
                          {categoryRules.map((rule) => (
                            <tr key={rule.rule_id} className="hover:bg-[#334155]/50 transition-colors duration-150">
                              <td className="px-4 py-3">
                                <div>
                                  <div className="text-sm font-medium text-white">{rule.name}</div>
                                  {rule.description && (
                                    <div className="text-xs text-[#94a3b8] mt-1">{rule.description}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-[#cbd5e1]">
                                  {Array.isArray(rule.conditions) ? (
                                    rule.conditions.map((cond, idx) => (
                                      <div key={idx} className="text-xs">
                                        <span className="font-mono text-[#60a5fa]">{cond.field}</span>
                                        <span className="mx-1 text-[#94a3b8]">{operatorLabels[cond.operator]}</span>
                                        <span className="font-semibold text-white">{cond.value}</span>
                                        {cond.value2 !== undefined && (
                                          <span className="text-[#94a3b8]"> and {cond.value2}</span>
                                        )}
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-xs text-[#94a3b8]">No conditions defined</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-sm font-bold text-white bg-[#374151] px-2 py-1 rounded">
                                  {rule.scoring.base_score}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-sm text-[#cbd5e1]">{rule.priority}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  rule.enabled
                                    ? 'bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30'
                                    : 'bg-[#374151] text-[#94a3b8] border border-[#4b5563]'
                                }`}>
                                  {rule.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <button
                                    onClick={() => handleEditRule(rule)}
                                    className="text-[#60a5fa] hover:text-[#818cf8] transition-colors duration-150"
                                    title="Edit"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRule(rule.rule_id)}
                                    className="text-[#ef4444] hover:text-[#f87171] transition-colors duration-150"
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Rule Edit Modal */}
        {showRuleModal && editingRule && (
          <RuleEditModal
            rule={editingRule}
            categories={categories}
            onSave={handleSaveRule}
            onClose={() => {
              setShowRuleModal(false);
              setEditingRule(null);
            }}
          />
        )}
      </div>
    </SuperAdminLayout>
  );
};

interface RuleEditModalProps {
  rule: ScoringRule;
  categories: Record<string, Category>;
  onSave: (rule: ScoringRule) => void;
  onClose: () => void;
}

const RuleEditModal: React.FC<RuleEditModalProps> = ({ rule, categories, onSave, onClose }) => {
  const [editedRule, setEditedRule] = useState<ScoringRule>(rule);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedRule);
  };

  const addCondition = () => {
    setEditedRule({
      ...editedRule,
      conditions: [
        ...editedRule.conditions,
        { field: '', operator: 'equals', value: '', value2: undefined }
      ]
    });
  };

  const removeCondition = (index: number) => {
    setEditedRule({
      ...editedRule,
      conditions: editedRule.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index: number, field: keyof typeof editedRule.conditions[0], value: any) => {
    const newConditions = [...editedRule.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setEditedRule({ ...editedRule, conditions: newConditions });
  };

  const addAdjustment = () => {
    setEditedRule({
      ...editedRule,
      scoring: {
        ...editedRule.scoring,
        adjustments: [
          ...editedRule.scoring.adjustments,
          { condition: { operator: 'equals', value: '' }, score: 50 }
        ]
      }
    });
  };

  const removeAdjustment = (index: number) => {
    setEditedRule({
      ...editedRule,
      scoring: {
        ...editedRule.scoring,
        adjustments: editedRule.scoring.adjustments.filter((_, i) => i !== index)
      }
    });
  };

  const updateAdjustment = (index: number, field: string, value: any) => {
    const newAdjustments = [...editedRule.scoring.adjustments];
    if (field.startsWith('condition.')) {
      const condField = field.replace('condition.', '');
      newAdjustments[index] = {
        ...newAdjustments[index],
        condition: { ...newAdjustments[index].condition, [condField]: value }
      };
    } else {
      newAdjustments[index] = { ...newAdjustments[index], [field]: value };
    }
    setEditedRule({
      ...editedRule,
      scoring: { ...editedRule.scoring, adjustments: newAdjustments }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-[#1e293b] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#374151] shadow-2xl">
        <div className="sticky top-0 bg-[#1e293b] border-b border-[#374151] px-6 py-4">
          <h2 className="text-xl font-semibold text-white">
            {rule.id ? 'Edit Rule' : 'Create New Rule'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
                Rule Name
              </label>
              <input
                type="text"
                value={editedRule.name}
                onChange={(e) => setEditedRule({ ...editedRule, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
                Category
              </label>
              <select
                value={editedRule.category}
                onChange={(e) => setEditedRule({ ...editedRule, category: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200"
              >
                {Object.entries(categories).map(([key, cat]) => (
                  <option key={key} value={key}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
              Description
            </label>
            <textarea
              value={editedRule.description || ''}
              onChange={(e) => setEditedRule({ ...editedRule, description: e.target.value })}
              className="w-full px-4 py-2.5 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af]"
              rows={2}
              placeholder="Optional description of the rule"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
                Base Score
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={editedRule.scoring.base_score}
                onChange={(e) => setEditedRule({
                  ...editedRule,
                  scoring: { ...editedRule.scoring, base_score: parseInt(e.target.value) }
                })}
                className="w-full px-4 py-2.5 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
                Priority
              </label>
              <input
                type="number"
                min="0"
                value={editedRule.priority}
                onChange={(e) => setEditedRule({ ...editedRule, priority: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#cbd5e1] mb-2">
                Status
              </label>
              <div className="flex items-center h-[42px]">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={editedRule.enabled}
                      onChange={(e) => setEditedRule({ ...editedRule, enabled: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${editedRule.enabled ? 'bg-[#6366f1]' : 'bg-[#374151]'}`}>
                      <div className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${editedRule.enabled ? 'translate-x-5' : ''}`}></div>
                    </div>
                  </div>
                  <span className="ml-3 text-sm text-[#cbd5e1]">Enabled</span>
                </label>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-[#cbd5e1]">
                Conditions
              </label>
              <button
                type="button"
                onClick={addCondition}
                className="text-sm text-[#60a5fa] hover:text-[#818cf8] transition-colors duration-150"
              >
                + Add Condition
              </button>
            </div>
            <div className="space-y-2">
              {editedRule.conditions.map((condition, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Field"
                    value={condition.field}
                    onChange={(e) => updateCondition(index, 'field', e.target.value)}
                    className="flex-1 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                  />
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                    className="px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 text-sm"
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                    <option value="between">Between</option>
                    <option value="contains">Contains</option>
                    <option value="exists">Exists</option>
                    <option value="not_exists">Not Exists</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Value"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(index, 'value', e.target.value)}
                    className="w-24 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                  />
                  {condition.operator === 'between' && (
                    <input
                      type="text"
                      placeholder="Value 2"
                      value={condition.value2 || ''}
                      onChange={(e) => updateCondition(index, 'value2', e.target.value)}
                      className="w-24 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="text-[#ef4444] hover:text-[#f87171] transition-colors duration-150"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Score Adjustments */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-[#cbd5e1]">
                Score Adjustments
              </label>
              <button
                type="button"
                onClick={addAdjustment}
                className="text-sm text-[#60a5fa] hover:text-[#818cf8] transition-colors duration-150"
              >
                + Add Adjustment
              </button>
            </div>
            <div className="space-y-2">
              {editedRule.scoring.adjustments.map((adjustment, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <select
                    value={adjustment.condition.operator}
                    onChange={(e) => updateAdjustment(index, 'condition.operator', e.target.value)}
                    className="px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 text-sm"
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                    <option value="between">Between</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Value"
                    value={adjustment.condition.value || ''}
                    onChange={(e) => updateAdjustment(index, 'condition.value', e.target.value)}
                    className="flex-1 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                  />
                  {adjustment.condition.operator === 'between' && (
                    <input
                      type="text"
                      placeholder="Value 2"
                      value={adjustment.condition.value2 || ''}
                      onChange={(e) => updateAdjustment(index, 'condition.value2', e.target.value)}
                      className="w-24 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                    />
                  )}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Score"
                    value={adjustment.score}
                    onChange={(e) => updateAdjustment(index, 'score', parseInt(e.target.value))}
                    className="w-24 px-3 py-2 bg-[#374151] border border-[#4b5563] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]/50 transition-all duration-200 placeholder-[#9ca3af] text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeAdjustment(index)}
                    className="text-[#ef4444] hover:text-[#f87171] transition-colors duration-150"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#374151]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-[#374151] text-[#cbd5e1] rounded-lg hover:bg-[#4b5563] hover:text-white transition-all duration-200 font-medium border border-[#4b5563]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2.5 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white rounded-lg hover:shadow-lg hover:shadow-[#6366f1]/25 transition-all duration-200 transform hover:-translate-y-0.5 font-medium"
            >
              {rule.id ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneralAnalysisScoringPage;